import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import DataTable from 'components/shared/DataTable';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import Search from 'components/shared/Search';
import { useAddressOptions } from 'hooks/useAddressOptions';
import type { Group } from 'models/flexible/Group';
import { useInfiniteAdminGroups } from 'services/api/admin/groups';
import { colors } from 'styles/theme/colors';
import TrashIcon from 'svg/TrashIcon';

type Props = {
  clientId?: string;
  groupTypeId: string;
  groupTypeApiName: string;
  groupTypeDisplayName: string;
  groups: Group[];
  handleClose: () => void;
  handleApply: (groupTypeId: string, groups: Group[]) => void;
  disabled?: boolean;
};

export default function AssociatedGroupsSlideout({
  clientId,
  groupTypeApiName,
  groupTypeId,
  groupTypeDisplayName,
  groups,
  handleClose,
  handleApply,
  disabled = false,
}: Props) {
  const [selectedGroups, setSelectedGroups] = useState<Group[]>(groups || []);
  const [search, setSearch] = useState('');

  const asyncGroupOptions = useInfiniteAdminGroups({
    type: groupTypeApiName,
    include: 'groupType',
    sortBy: 'name',
    ...(clientId ? { 'clientId.not': clientId } : {}),
  });

  const addressProps = useAddressOptions();
  const selectedIds = useMemo(() => new Set(selectedGroups.map((g) => g.id)), [selectedGroups]);

  const tableData = useMemo(
    () => selectedGroups.filter((g) => g.name.toLowerCase().includes(search.toLowerCase())),
    [selectedGroups, search]
  );

  const columnHelper = createColumnHelper<Group>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: groupTypeDisplayName,
        cell: (info) => <strong>{info.getValue()}</strong>,
      }),
      columnHelper.accessor('client.name', {
        header: 'Client',
        cell: (info) => (
          <ClientCell>
            <strong>{info.getValue()}</strong>
            {!disabled && (
              <TrashContainer
                onClick={() => setSelectedGroups((prev) => prev.filter((g) => g.id !== info.row.original.id))}>
                <TrashIcon color={colors.accentRed} width={16} height={16} style={{ cursor: 'pointer' }} />
              </TrashContainer>
            )}
          </ClientCell>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupTypeDisplayName, disabled]
  );

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>{groupTypeDisplayName}</PanelTitle>
        <CloseButton onClick={handleClose}>&times;</CloseButton>
      </PanelHeader>
      <PanelContent>
        {!disabled && (
          <InputGroup title={`Select ${groupTypeDisplayName}s`}>
            <Select<Group>
              {...asyncGroupOptions}
              {...addressProps}
              value={null}
              onChange={(g) => g && setSelectedGroups((prev) => [...prev, g])}
              getOptionValue={(o) => o.id}
              placeholder='Select'
              filterOption={(option: any) => !selectedIds.has(option.data.id)}
            />
          </InputGroup>
        )}
        <TableBar>
          <ItemsCount>
            {selectedGroups.length} Associated {groupTypeDisplayName}s
          </ItemsCount>
          <Search value={search} onChange={setSearch} placeholder='Search...' />
        </TableBar>
        <DataTable columns={columns} data={tableData} defaultSortBy={[{ id: 'name', desc: false }]} hasFooter={false} />
      </PanelContent>
      <PanelFooter>
        <ButtonGroup>
          <Button variant='ghost' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleApply(groupTypeId, selectedGroups);
              handleClose();
            }}
            disabled={disabled}>
            Apply
          </Button>
        </ButtonGroup>
      </PanelFooter>
    </PanelContainer>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;

const TableBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  > :last-child {
    max-width: 240px;
  }
`;

const ItemsCount = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.black};
  white-space: nowrap;
`;

const ClientCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TrashContainer = styled.div`
  display: flex;
  align-items: center;
`;
